class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this._pendingRequests = new Map();
  }

  /* ============================================================
   * V12 — Optimistic UI for Add-to-Cart
   * addOptimistic / reconcile / rollbackOptimistic
   * Drawer opens with a placeholder line item BEFORE /cart/add.js
   * resolves; reconcile() patches diffs silently when response arrives.
   * Rollback path removes the optimistic line if server rejects.
   * ============================================================ */

  addOptimistic(payload, requestId) {
    if (!payload || !requestId) return false;
    try {
      // Snapshot empty-state shell before mutating, so rollback can restore it
      if (this.classList.contains('is-empty')) {
        const innerEl = this.querySelector('.drawer__inner');
        if (innerEl) this._emptySnapshot = innerEl.cloneNode(true);
        const shellTpl = document.getElementById('CartDrawer-OptimisticEmptyShell');
        if (shellTpl && innerEl) {
          const shellNode = shellTpl.content.cloneNode(true);
          innerEl.replaceWith(shellNode.firstElementChild);
          this.classList.remove('is-empty');
          const cartFooter = document.getElementById('main-cart-footer');
          if (cartFooter) cartFooter.classList.remove('is-empty');
        }
      }

      const tpl = document.getElementById('CartDrawer-OptimisticLineTemplate');
      if (!tpl) return false;
      const node = tpl.content.firstElementChild.cloneNode(true);
      this._fillOptimisticTemplate(node, payload);
      node.dataset.requestId = requestId;
      node.dataset.variantId = String(payload.variantId || '');

      // Find the right insertion point. Try table.cart-items tbody first
      // (standard Dawn). If absent, try alternate locations the custom drawer
      // may use. If still absent, OPEN the drawer anyway and let reconcile()
      // do the full render — better UX than silently bailing to legacy flow.
      let insertionTarget = this.querySelector('table.cart-items tbody');
      if (!insertionTarget) insertionTarget = this.querySelector('cart-drawer-items table tbody');
      if (!insertionTarget) insertionTarget = this.querySelector('.drawer__cart-items-wrapper tbody');
      if (!insertionTarget) {
        // Last resort: open the drawer immediately so user sees feedback,
        // even though we can't insert the optimistic line. The fetch will
        // resolve in 2-3s and reconcile() will full-render the drawer.
        console.warn('[optimistic] no tbody found, opening drawer without optimistic line');
        this.setAttribute('data-optimistic-active', '1');
        this._pendingRequests.set(requestId, { node: null, payload, ts: Date.now() });
        this._incrementBubble(payload.quantity || 1);
        this.open();
        return true; // tell caller to call reconcile() (not renderContents) when fetch resolves
      }
      insertionTarget.prepend(node);
      this.setAttribute('data-optimistic-active', '1');
      this._pendingRequests.set(requestId, { node, payload, ts: Date.now() });
      this._incrementBubble(payload.quantity || 1);
      this.open();
      return true;
    } catch (e) {
      console.warn('[optimistic] addOptimistic failed', e);
      return false;
    }
  }

  reconcile(response, requestId) {
    const pending = this._pendingRequests.get(requestId);
    this._pendingRequests.delete(requestId);

    try {
      // Detect partial stock-shortfall: optimistic asked for N, server gave fewer
      const errEl = document.getElementById('CartDrawer-CartErrors');
      if (pending && response.items && errEl) {
        const variantId = pending.payload.variantId;
        const serverItem = response.items.find((it) => Number(it.variant_id) === variantId);
        if (serverItem && serverItem.quantity < pending.payload.quantity) {
          errEl.textContent = `Only ${serverItem.quantity} of "${pending.payload.title}" added (limited stock).`;
        } else {
          errEl.textContent = '';
        }
      }

      const sectionHtml = response && response.sections && response.sections['cart-drawer'];
      if (!sectionHtml) {
        // No section in response; fall back to full re-render via existing path
        if (typeof this.renderContents === 'function') this.renderContents(response);
        return;
      }

      const newDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const matches = this._fingerprintMatches(response);

      if (matches && pending) {
        // Promote optimistic node to real, patch image/price if server differs
        this._promoteOptimisticNode(pending.node, response, newDoc);
        // Patch cart count bubble + totals + free-shipping bar from response
        this._patchSectionFragments(newDoc);
        // Refresh cart-anchor-driven "You May Also Like" (sits outside the patched fragments)
        const ymalSrc = newDoc.querySelector('.cart_product_main');
        const ymalTarget = document.querySelector('.cart_product_main');
        if (ymalSrc && ymalTarget) ymalTarget.replaceWith(ymalSrc);
        // Re-enable any GoKwik buttons that were rendered disabled
        requestAnimationFrame(() => {
          if (typeof initCartSplide === 'function') initCartSplide();
        });
      } else {
        // Fingerprint mismatch (price diff, line merged, BYOB discount kicked in)
        // → full innerHTML swap. Drawer is already open so skip animation re-trigger.
        const sectionEl = newDoc.querySelector('#CartDrawer');
        if (sectionEl) {
          const target = document.querySelector('#CartDrawer');
          if (target) target.innerHTML = sectionEl.innerHTML;
        }
        // Patch icon bubble too
        const bubbleSection = response.sections && response.sections['cart-icon-bubble'];
        if (bubbleSection) {
          const bubbleTarget = document.getElementById('cart-icon-bubble');
          const bubbleParsed = new DOMParser().parseFromString(bubbleSection, 'text/html');
          const bubbleSrc = bubbleParsed.querySelector('.shopify-section') || bubbleParsed.body.firstElementChild;
          if (bubbleTarget && bubbleSrc) bubbleTarget.innerHTML = bubbleSrc.innerHTML;
        }
        requestAnimationFrame(() => {
          if (typeof initCartSplide === 'function') initCartSplide();
        });
      }

      this._emptySnapshot = null;
      if (this._pendingRequests.size === 0) this.removeAttribute('data-optimistic-active');
    } catch (e) {
      console.warn('[optimistic] reconcile failed, falling back', e);
      if (typeof this.renderContents === 'function') this.renderContents(response);
    }
  }

  rollbackOptimistic(requestId, errorMsg) {
    const pending = this._pendingRequests.get(requestId);
    this._pendingRequests.delete(requestId);
    if (!pending) return;

    try {
      if (pending.node && pending.node.parentNode) pending.node.remove();
      this._incrementBubble(-(pending.payload.quantity || 1));

      const remainingRows = this.querySelectorAll('table.cart-items tbody tr');
      if (remainingRows.length === 0 && this._emptySnapshot) {
        // Restore pre-optimistic empty-state shell
        const currentInner = this.querySelector('.drawer__inner');
        if (currentInner) currentInner.replaceWith(this._emptySnapshot);
        this.classList.add('is-empty');
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.add('is-empty');
      }

      this._emptySnapshot = null;
      if (this._pendingRequests.size === 0) this.removeAttribute('data-optimistic-active');

      if (errorMsg) {
        const errEl = document.getElementById('CartDrawer-CartErrors');
        if (errEl) errEl.textContent = errorMsg;
      }
    } catch (e) {
      console.warn('[optimistic] rollback failed', e);
    }
  }

  _fillOptimisticTemplate(node, payload) {
    const setSlot = (sel, fn) => {
      const el = node.querySelector(sel);
      if (el) fn(el);
    };
    setSlot('[data-slot="image-link"]', (el) => {
      if (payload.url) el.setAttribute('href', payload.url);
    });
    setSlot('[data-slot="image-wrap"]', (wrap) => {
      // Card-listing images and cart-thumbnail images come from different
      // Shopify uploads. Skip optimistic image (would flicker when real image
      // arrives) — render a subtle pulsing skeleton sized via .cart-item__image
      // so it matches the real thumbnail dimensions. Real image fades in when
      // reconcile() swaps the server-rendered <tr>.
      const placeholder = document.createElement('div');
      placeholder.className = 'cart-item__image cart-item__image--skeleton';
      placeholder.setAttribute('aria-hidden', 'true');
      wrap.appendChild(placeholder);
    });
    setSlot('[data-slot="title-link"]', (el) => {
      el.textContent = payload.title || '';
      if (payload.url) el.setAttribute('href', payload.url);
    });
    setSlot('[data-slot="subtitle"]', (el) => {
      el.textContent = payload.subtitle || '';
    });
    setSlot('[data-slot="price"]', (el) => {
      if (payload.priceFormatted) el.textContent = payload.priceFormatted;
    });
    setSlot('[data-slot="compare-at"]', (el) => {
      if (payload.compareAtFormatted) {
        el.textContent = payload.compareAtFormatted;
        el.removeAttribute('hidden');
      }
    });
    setSlot('[data-slot="total"]', (el) => {
      if (payload.priceFormatted) el.textContent = payload.priceFormatted;
    });
    setSlot('[data-slot="quantity"]', (el) => {
      el.textContent = String(payload.quantity || 1);
    });
  }

  _fingerprintMatches(response) {
    // True if the response's item count equals the rendered row count
    // (real existing rows + still-pending optimistic rows we haven't reconciled yet).
    if (!response || typeof response.item_count !== 'number') return false;
    const rendered = this.querySelectorAll('table.cart-items tbody tr').length;
    // Allow drift up to 0 — strict match. Tightening reduces false-positive silent patches
    // that would skip needed full re-render (e.g. line merged with existing variant).
    return rendered === response.item_count;
  }

  _promoteOptimisticNode(node, response, newDoc) {
    if (!node || !node.parentNode) return;
    // Find the new line in response.items by variant id
    const variantId = node.dataset.variantId ? Number(node.dataset.variantId) : null;
    if (!variantId || !response.items) return;
    const idx = response.items.findIndex((it) => Number(it.variant_id) === variantId);
    if (idx === -1) return;
    const realRow = newDoc.querySelector(`#CartDrawer-Item-${idx + 1}`);
    if (realRow) {
      // Swap optimistic node with the server-rendered row (preserves the prepend position)
      node.replaceWith(realRow);
    } else {
      // Real row not findable; just promote the id and clear the optimistic flag
      node.id = `CartDrawer-Item-${idx + 1}`;
      delete node.dataset.optimistic;
      delete node.dataset.requestId;
    }
    // Mirror server-truth quantity onto the line (matters when stock-shortfall
    // returned a smaller qty than requested — avoids stale optimistic count).
    const serverItem = response.items[idx];
    if (serverItem && typeof serverItem.quantity === 'number') {
      const qtySlot = node.parentNode && node.parentNode.querySelector
        ? null
        : null; // node is replaced; can't reach. Quantity already correct in realRow.
      // The cart-icon-bubble patch below will reflect the true total count.
    }
  }

  _patchSectionFragments(newDoc) {
    // Patch cart icon bubble + free-shipping bar + totals from the new document
    // without re-rendering the whole drawer.
    const patches = [
      ['.cart-count-bubble', '.cart-count-bubble'],
      ['.shipping-bar', '.shipping-bar'],
      ['.cart-drawer__footer .totals', '.cart-drawer__footer .totals'],
    ];
    patches.forEach(([targetSel, srcSel]) => {
      const target = document.querySelector(targetSel);
      const src = newDoc.querySelector(srcSel);
      if (target && src) target.innerHTML = src.innerHTML;
    });
  }

  _incrementBubble(delta) {
    const bubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (bubble) {
      const current = parseInt(bubble.textContent, 10) || 0;
      bubble.textContent = String(Math.max(0, current + delta));
    }
    try {
      const cached = parseInt(localStorage.getItem('everaw.cartBubble') || '0', 10);
      localStorage.setItem('everaw.cartBubble', String(Math.max(0, cached + delta)));
    } catch (e) { /* localStorage unavailable */ }
  }
  /* ============================================================
   * End V12 optimistic UI
   * ============================================================ */

  setHeaderCartIconAccessibility() {
    // V12 FIX: <cart-drawer> renders earlier in HTML (~22% in) than
    // <a id="cart-icon-bubble"> (~36% in). When the constructor fired,
    // the bubble didn't exist yet → handler never attached → mobile users
    // couldn't open the drawer by tapping the cart icon.
    // Solution: try immediately, then on DOMContentLoaded, then via
    // MutationObserver as final safety net.
    const attach = () => {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return false;
      if (cartLink.dataset.everawCartListenerAttached === '1') return true;
      cartLink.dataset.everawCartListenerAttached = '1';

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
      return true;
    };

    if (attach()) return;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => { if (!attach()) this._observeCartIcon(attach); });
    } else {
      this._observeCartIcon(attach);
    }
  }

  _observeCartIcon(attach) {
    if (attach()) return;
    const obs = new MutationObserver(() => { if (attach()) obs.disconnect(); });
    obs.observe(document.body, { childList: true, subtree: true });
    // Safety: stop observing after 10s no matter what
    setTimeout(() => obs.disconnect(), 10000);
  }

  open(triggeredBy) {
    // CRITICAL: drawer must visibly open even if downstream code throws
    // (Splide, focus trap, etc.) — wrap each step in try/catch so a single
    // failure can't prevent the .active class from being added.
    try { if (triggeredBy) this.setActiveElement(triggeredBy); } catch(e) {}
    try {
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    } catch(e) {}
    // Use rAF to ensure the browser has a frame to apply initial styles before animating
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    // Splide init can throw on PDPs where suggested-products list isn't in DOM yet.
    // Don't let that prevent the drawer from being visible.
    try {
      if (typeof initCartSplide === 'function') initCartSplide();
    } catch(e) { console.warn('[cart-drawer] initCartSplide failed', e); }

    this.addEventListener(
      'transitionend',
      () => {
        try {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          if (containerToTrapFocusOn && typeof trapFocus === 'function') trapFocus(containerToTrapFocusOn, focusElement);
        } catch(e) { console.warn('[cart-drawer] focus trap failed', e); }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
